import { Controller } from "@hotwired/stimulus"
import { readFormParams, dataJSON, nextLabelSpan, prevSpan, buildPriceFormatting, renderPrice } from "./shared"

// One instance per `.nested-fields` accommodation row (see
// app/views/shared/_user_room_fields.html.erb). Stimulus's connect() fires
// automatically as Cocoon inserts rows, replacing the legacy code's manual
// `cocoon:after-insert` wiring. Removal still needs an explicit
// `cocoon:after-remove` listener (see connect()) rather than relying on
// disconnect(): for a persisted row, Cocoon only hides it (sets `_destroy=1`)
// rather than removing it from the DOM, so disconnect() never fires for it.
export default class extends Controller {
  connect() {
    this.formParams = readFormParams(this.element)
    this.fmt = buildPriceFormatting(this.formParams)
    this.afId = this.element.dataset.afId

    this.nightsElem = this.element.querySelector("input.nights-item")
    this.hotelsElems = Array.from(this.element.querySelectorAll(".user_registration_user_fields_user_rooms_hotel"))
    this.roomsElems = Array.from(this.element.querySelectorAll(".user_registration_user_fields_user_rooms_room"))
    this.bedsElems = Array.from(this.element.querySelectorAll(".user_registration_user_fields_user_rooms_beds"))

    this.$element = window.jQuery(this.element)
    this.$element.on("apply.daterangepicker", ".picker", (ev, picker) => this._toggleAccommodation(picker))
    this.$element.on("cancel.daterangepicker", ".picker", () => this._clearAccommodation())

    // Cocoon triggers `cocoon:after-remove` on the removed row's *parent*
    // (`.nested-body`), passing the removed row as the event's extra data —
    // never on the row itself — so this has to be bound on the parent and
    // filtered down to this row. For a persisted row, cocoon only hides it
    // (sets `_destroy=1`) rather than removing it from the DOM, so it would
    // otherwise keep contributing its stale price to the total forever.
    this.$parent = window.jQuery(this.element.parentElement)
    this._onCocoonAfterRemove = this._onCocoonAfterRemove.bind(this)
    this.$parent.on("cocoon:after-remove", this._onCocoonAfterRemove)

    this._onDelegatedChange = this._onDelegatedChange.bind(this)
    this._onPriceChange = this._onPriceChange.bind(this)
    this.element.addEventListener("change", this._onDelegatedChange)
    this.element.addEventListener("registration:priceChange", this._onPriceChange)

    window.initDatePickers(this.element.querySelectorAll(".picker"))

    // Refresh this row's room-item pricing against the currently selected
    // term/promo group — needed for rows added dynamically after initial load.
    this.element.dispatchEvent(new CustomEvent("registration:refresh", { bubbles: true }))

    if (this.element.querySelectorAll("input.hotel-item:checked").length === 0) {
      this._clearAccommodation()
    } else {
      this.element.querySelectorAll("input.hotel-item:checked").forEach((el) => el.dispatchEvent(new Event("change", { bubbles: true })))
      this.element.querySelectorAll("input.room-item:checked").forEach((el) => el.dispatchEvent(new Event("change", { bubbles: true })))
      const bedsSelected = this.element.querySelector(".beds-selected")?.dataset.selected || "0"
      const bedInput = this.element.querySelector(`input.bed-item[value="${bedsSelected}"]`)
      if (bedInput) {
        bedInput.checked = true
        bedInput.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }
  }

  disconnect() {
    this.$element?.off()
    this.$parent?.off("cocoon:after-remove", this._onCocoonAfterRemove)
    this.element.removeEventListener("change", this._onDelegatedChange)
    this.element.removeEventListener("registration:priceChange", this._onPriceChange)
  }

  _onDelegatedChange(event) {
    const target = event.target
    if (target.matches(".hotel-item")) {
      this._toggleHotels(target)
    } else if (target.matches(".room-item")) {
      this._toggleRoomExtra(target)
      this._setTotalAccommodationPart()
    } else if (target.matches(".bed-item")) {
      this._toggleRoomPart(parseInt(target.value) || 0)
      this._setTotalAccommodationPart()
    }
  }

  _onPriceChange(event) {
    if (!event.target.matches(".room-item")) return
    this._toggleRoomPrices(event.target)
    this._setTotalAccommodationPart()
  }

  _onCocoonAfterRemove(event, deletedItem) {
    if (!deletedItem || deletedItem[0] !== this.element) return

    const picker = this.element.querySelector(".picker")
    if (picker) window.jQuery(picker).trigger("cancel.daterangepicker")
    this.element.querySelectorAll("input, select, textarea").forEach((el) => { el.required = false })

    // For a "dynamic" (unpersisted) row, cocoon has already detached it from
    // the DOM by this point, so `this.element` has no parent to bubble a
    // CustomEvent through. Dispatch from the (still-attached) parent instead
    // so the pricing controller's total recount actually receives it.
    this.$parent[0]?.dispatchEvent(new CustomEvent("registration:accommodationTotalChanged", { bubbles: true }))
  }

  _toggleAccommodation(picker) {
    const startD = moment(picker.startDate).startOf("day")
    const endD = moment(picker.endDate).startOf("day")
    const days = endD.diff(startD, "days")
    if (days === 0) {
      window.jQuery(picker.element).trigger("cancel.daterangepicker", picker)
      return
    }
    if (this.nightsElem) this.nightsElem.value = days
    this.hotelsElems.forEach((el) => {
      el.classList.remove("hidden-block")
      prevSpan(el)?.classList.remove("hidden-block")
    })
    this._filterRooms(startD, endD)
    this._setTotalAccommodationPart()
  }

  _clearAccommodation() {
    if (this.nightsElem) this.nightsElem.value = ""
    this.roomsElems.forEach((el) => {
      el.querySelectorAll("input").forEach((input) => { input.checked = false })
      el.classList.add("hidden-block")
      prevSpan(el)?.classList.add("hidden-block")
    })
    this.hotelsElems.forEach((el) => {
      el.querySelectorAll("input").forEach((input) => { input.checked = false })
      el.classList.add("hidden-block")
      prevSpan(el)?.classList.add("hidden-block")
    })
    this.element.querySelectorAll(".hidden-beds").forEach((el) => el.classList.add("hidden-block"))
    this._setTotalAccommodationPart()
  }

  _toggleRoomExtra(item) {
    this._toggleRoomPrices(item)
    const span = item.closest(".form-check")?.querySelector("span")
    const maxSplit = span ? parseInt(span.dataset.maxSplit || 0) : 0
    const beds = this.bedsElems.flatMap((el) => Array.from(el.querySelectorAll("input.bed-item")))

    beds.forEach((el) => {
      const val = parseInt(el.value)
      el.closest(".form-check")?.classList.toggle("hidden-block", val > maxSplit)
    })
    if (beds[0]) beds[0].checked = true

    if (maxSplit === 0) {
      this.element.querySelectorAll(".hidden-beds").forEach((el) => el.classList.add("hidden-block"))
    } else {
      this.element.querySelectorAll(".hidden-beds:not(.hidden-roommate)").forEach((el) => el.classList.remove("hidden-block"))
      this.element.querySelectorAll(".hidden-roommate").forEach((el) => el.classList.add("hidden-block"))
    }
    this.element.querySelectorAll(".hidden-roommate.room-required input, .hidden-roommate.room-required select, .hidden-roommate.room-required textarea")
      .forEach((el) => { el.required = false })
  }

  _toggleRoomPrices(item) {
    const beds = this.element.querySelectorAll(".user_registration_user_fields_user_rooms_beds input.bed-item")
    const values = dataJSON(item, "priceValues") || {}
    const termChecked = this.element.closest("form")?.querySelector(".price-term-select:checked")
    const termIndex = termChecked ? termChecked.value : this.formParams.first_term
    const termValues = values[termIndex] || {}
    const partValues = termValues[item.value] || {}
    const fullPrice = partValues.full || 0
    const partPrice = partValues.part || 0

    beds.forEach((el) => {
      const val = parseInt(el.value)
      const value = val === 0 ? fullPrice : partPrice * val
      const span = nextLabelSpan(el)
      if (span) span.innerHTML = renderPrice(this.fmt, value)
    })
  }

  _toggleRoomPart(beds) {
    const allBeds = beds === 0
    this.element.querySelectorAll(".hidden-roommate").forEach((el) => el.classList.toggle("hidden-block", allBeds))
    this.element.querySelectorAll(".hidden-roommate.room-required input, .hidden-roommate.room-required select, .hidden-roommate.room-required textarea")
      .forEach((el) => { el.required = !allBeds })
  }

  _toggleHotels(item) {
    const checked = item.checked
    const hotelId = item.value

    this.roomsElems.forEach((el) => {
      el.classList.toggle("hidden-block", !checked)
      prevSpan(el)?.classList.toggle("hidden-block", !checked)
      el.querySelectorAll(".form-check").forEach((fc) => fc.classList.add("hidden-block"))
    })
    if (checked) {
      this.roomsElems.forEach((el) => {
        el.querySelectorAll(`.form-check:not(.disabled-room) span[data-hotel="${hotelId}"]`).forEach((span) => {
          span.closest(".form-check")?.classList.remove("hidden-block")
        })
      })
    }
    this.roomsElems.forEach((el) => {
      el.querySelectorAll(".form-check.hidden-block input").forEach((input) => { input.checked = false })
    })
    this._setTotalAccommodationPart()
  }

  _setTotalAccommodationPart() {
    const bedChecked = this.element.querySelector(".user_registration_user_fields_user_rooms_beds input:checked")
    const beds = bedChecked ? parseInt(bedChecked.value) : NaN
    const nightCount = this.nightsElem ? parseFloat(this.nightsElem.value || 0) : 0
    const roomChecked = this.element.querySelector(".user_registration_user_fields_user_rooms_room input:checked")
    const span = roomChecked ? nextLabelSpan(roomChecked) : null

    let value
    let bedCount
    if (beds === 0) {
      value = span ? parseFloat(span.dataset.val || 0) : 0
      bedCount = 1
    } else {
      value = span ? parseFloat(span.dataset.partVal || 0) : 0
      bedCount = beds || 0
    }
    const total = nightCount * bedCount * value
    if (this.nightsElem) this.nightsElem.dataset.hotelPrice = total || 0
    this.element.dispatchEvent(new CustomEvent("registration:accommodationTotalChanged", { bubbles: true }))
  }

  _filterRooms(startDate, endDate) {
    if (this.formParams.full_edit) return

    const availableTerms = (this.formParams.available_capacities && this.formParams.available_capacities[this.afId]) || []
    const overlapping = availableTerms.filter((item) => {
      const itemStart = moment(item.from)
      const itemEnd = moment(item.till)
      return startDate.isSameOrAfter(itemStart) && endDate.isSameOrBefore(itemEnd)
    })

    let availableHotels = []
    const availableRooms = {}
    overlapping.forEach((item) => {
      availableHotels.push(...(item.hotels || []))
      Object.keys(item.rooms || {}).forEach((roomId) => {
        const roomItem = item.rooms[roomId]
        if (!availableRooms[roomId] || availableRooms[roomId] < roomItem) {
          availableRooms[roomId] = roomItem
        }
      })
    })
    availableHotels = Array.from(new Set(availableHotels))

    this.hotelsElems.forEach((el) => {
      el.querySelectorAll("input").forEach((input) => {
        input.closest(".form-check")?.classList.toggle("hidden-block", !availableHotels.includes(parseInt(input.value)))
      })
      el.querySelectorAll("input").forEach((input) => { input.checked = false })
    })

    this.roomsElems.forEach((el) => {
      el.querySelectorAll("input").forEach((input) => { input.checked = false })
      el.classList.add("hidden-block")
      prevSpan(el)?.classList.add("hidden-block")
      el.querySelectorAll(".form-check").forEach((fc) => fc.classList.add("hidden-block"))
      el.querySelectorAll("input").forEach((input) => {
        const capacity = availableRooms[input.value]
        input.closest(".form-check")?.classList.toggle("disabled-room", !capacity)
      })
    })

    this.bedsElems.forEach((el) => {
      el.querySelectorAll("input").forEach((input) => { input.checked = false })
      el.classList.add("hidden-block")
      prevSpan(el)?.classList.add("hidden-block")
      el.querySelectorAll(".form-check").forEach((fc) => fc.classList.add("hidden-block"))
    })
    this._toggleRoomPart(0)
  }
}
